import { useMutation, useQuery, useQueryClient } from "@tanstack/react-query";
import {
	Badge,
	Button,
	Card,
	FormField,
	Input,
	Modal,
	Page,
	Select,
	Table,
	type TableColumn,
	Textarea,
	useToast,
} from "@theg1group/ui";
import { useState } from "react";
import { Link, useNavigate } from "react-router-dom";
import { api } from "../lib/api.ts";
import { fmtDate } from "../lib/format.ts";
import type { CaseRow, PartnerRow, Rows } from "../lib/types.ts";

export function Cases() {
	const navigate = useNavigate();
	const [q, setQ] = useState("");
	const [showNew, setShowNew] = useState(false);
	const list = useQuery({
		queryKey: ["cases", q],
		queryFn: () => api.get<Rows<CaseRow>>(`/api/cases?q=${encodeURIComponent(q)}`),
	});

	const columns: TableColumn<CaseRow>[] = [
		{
			id: "name",
			header: "Case",
			accessor: (r) => (
				<Link to={`/cases/${r.id}`} className="text-primary hover:underline font-medium">
					{r.name}
				</Link>
			),
		},
		{ id: "status", header: "Status", accessor: (r) => <Badge>{r.status}</Badge> },
		{
			id: "partner",
			header: "Partner",
			accessor: (r) =>
				r.partnerId ? (
					<Link to={`/partners/${r.partnerId}`} className="text-primary hover:underline">
						{r.partnerName ?? r.partnerId.slice(0, 8)}
					</Link>
				) : (
					"—"
				),
		},
		{ id: "items", header: "Items", accessor: (r) => r.itemCount },
		{ id: "findings", header: "Findings", accessor: (r) => r.findingCount },
		{ id: "created", header: "Created", accessor: (r) => fmtDate(r.createdAt, false) },
	];

	return (
		<Page
			title="Cases"
			description="Investigation folders: addresses, partners, transfers and findings bundled for export."
			actions={
				<Button variant="primary" onClick={() => setShowNew(true)}>
					New case
				</Button>
			}
		>
			<div className="mb-3 w-72">
				<Input value={q} onChange={(e) => setQ(e.target.value)} placeholder="Search cases…" />
			</div>
			<Card>
				<div className="overflow-x-auto">
					<Table
						columns={columns}
						data={list.data?.rows ?? []}
						keyExtractor={(r) => r.id}
						loading={list.isLoading}
						emptyMessage="No cases yet."
					/>
				</div>
			</Card>
			<NewCaseModal
				open={showNew}
				onClose={() => setShowNew(false)}
				onCreated={(id) => navigate(`/cases/${id}`)}
			/>
		</Page>
	);
}

function NewCaseModal({
	open,
	onClose,
	onCreated,
}: { open: boolean; onClose: () => void; onCreated: (id: string) => void }) {
	const qc = useQueryClient();
	const toast = useToast();
	const [name, setName] = useState("");
	const [partnerId, setPartnerId] = useState("");
	const [notes, setNotes] = useState("");
	const partners = useQuery({
		queryKey: ["partners", "", ""],
		queryFn: () => api.get<Rows<PartnerRow>>("/api/partners?q=&status="),
		enabled: open,
	});
	const create = useMutation({
		mutationFn: () =>
			api.post<CaseRow>("/api/cases", {
				name: name.trim(),
				partnerId: partnerId || undefined,
				notes: notes.trim() || undefined,
			}),
		onSuccess: (c) => {
			toast.addToast("success", "Case created");
			qc.invalidateQueries({ queryKey: ["cases"] });
			setName("");
			setNotes("");
			onClose();
			onCreated(c.id);
		},
		onError: (e) => toast.addToast("error", String(e)),
	});
	return (
		<Modal
			open={open}
			onClose={onClose}
			title="New case"
			footer={
				<div className="flex justify-end gap-2">
					<Button variant="ghost" onClick={onClose}>
						Cancel
					</Button>
					<Button
						variant="primary"
						disabled={!name.trim()}
						loading={create.isPending}
						onClick={() => create.mutate()}
					>
						Create
					</Button>
				</div>
			}
		>
			<div className="flex flex-col gap-3">
				<FormField label="Name" htmlFor="nc-name" required>
					<Input id="nc-name" value={name} onChange={(e) => setName(e.target.value)} />
				</FormField>
				<FormField label="Partner" htmlFor="nc-partner">
					<Select
						id="nc-partner"
						value={partnerId}
						onChange={(e) => setPartnerId(e.target.value)}
						options={[
							{ value: "", label: "— none —" },
							...(partners.data?.rows ?? []).map((p) => ({ value: p.id, label: p.name })),
						]}
					/>
				</FormField>
				<FormField label="Notes" htmlFor="nc-notes">
					<Textarea
						id="nc-notes"
						value={notes}
						onChange={(e) => setNotes(e.target.value)}
						className="min-h-[90px]"
					/>
				</FormField>
			</div>
		</Modal>
	);
}
